import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';

import { createRequest } from '@/api/fetch';
import type { BookToplistingRequest, BookToplistingResponse } from '@/types';

export enum ContractModel {
  ABO = 'ABO',
  DAILY = 'DAILY',
  PPA_BUNDLE = 'PPA_BUNDLE',
  LIFETIME = 'LIFETIME',
}

export interface ToplistingPrices {
  priceToplistingRent: number;
  priceToplistingBuy: number;
  priceToplistingPremium: number;
}

export interface TopListingAbos {
  totalSlots: number;
  associatedListerIds: Array<string>;
  activeSlots: number;
}

interface TopListingAbosResponse {
  data: {
    summary: TopListingAbos;
  };
}

export interface ToplistingCreditUsage {
  creditUsageForThisBookingInCHFCents: number;
  amountNotCoveredByCreditInCHFCents: number;
  agencyId: string;
  initialCreditInCHFCents: number;
  availableCreditInCHFCents: number;
  usedCreditInCHFCents: number;
  periodStartDate: string;
  periodEndDate: string;
  maximumTopListingDays: number;
  maximumPremiumListingDays: number;
  nextCreditInCHFCents: number;
  totalCostForThisBookingInCHFCents: number;
  nextCreditAvailableDate: string | null;
}

export interface LifetimeToplistingCreditUsage {
  availableCreditInCHFCents?: number;
  availableCreditAfterBookingTLRentInCHFCents?: number;
  availableCreditAfterBookingTLBuyInCHFCents?: number;
}

const createToplistingRequest = (method: 'GET' | 'PUT' | 'DELETE') =>
  createRequest({
    method,
    credentials: 'omit',
  });

export async function fetchTopListingAbos(listerId: string): Promise<TopListingAbos> {
  const endpoint = `${import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL}/listers/${listerId}/tlabos`;

  const response = await createToplistingRequest('GET')(endpoint);
  const responseBody: TopListingAbosResponse = await response.json();

  return responseBody.data.summary;
}

export const bookToplisting = async (
  propertyReferenceId: string,
  toplistingData: BookToplistingRequest,
): Promise<BookToplistingResponse> => {
  const endpoint = `${
    import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL
  }/toplistings/${encodeURIComponent(propertyReferenceId)}`;

  const response = await createToplistingRequest('PUT')(endpoint, JSON.stringify(toplistingData));

  return response.json();
};

export const bookToplistingAbo = async (
  propertyReferenceId: string,
  listerId: string,
): Promise<BookToplistingResponse> => {
  const endpoint = `${
    import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL
  }/listers/${listerId}/toplistings/${encodeURIComponent(propertyReferenceId)}`;

  const body = {
    contractModel: ContractModel.ABO,
    productType: RankType.Top,
  };

  const response = await createToplistingRequest('PUT')(endpoint, JSON.stringify(body));

  return response.json();
};

export const deleteToplisting = async (propertyReferenceId: string): Promise<void> => {
  const endpoint = `${
    import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL
  }/toplistings/${encodeURIComponent(propertyReferenceId)}`;

  const response = await createToplistingRequest('DELETE')(endpoint);

  return response.json();
};

export const fetchToplistingCreditUsage = async (
  offerType: OfferType,
  rankType: RankType,
  beginRequest: string,
  endRequest: string | null,
): Promise<ToplistingCreditUsage> => {
  let endpoint = `${import.meta.env.VUE_APP_TRANSACTION_SERVICE_API_BASE_URL}/credit-usage?`;
  endpoint += `offerType=${offerType}`;
  endpoint += `&productType=${rankType}`;
  endpoint += `&toplistingFromDate=${beginRequest}`;
  if (endRequest) {
    endpoint += `&toplistingToDate=${endRequest}`;
  }

  // NOTE: Always TRUE, until credit_info is removed from the backend
  endpoint += `&useCreditPeriod=true`;

  const response = await createToplistingRequest('GET')(endpoint);
  const responseBody: ToplistingCreditUsage = await response.json();

  return responseBody;
};

export const fetchLifetimeToplistingCreditUsage =
  async (): Promise<LifetimeToplistingCreditUsage> => {
    const endpoint = `${import.meta.env.VUE_APP_TRANSACTION_SERVICE_API_BASE_URL}/credit-usage-lifetime-tl`;
    const response = await createToplistingRequest('GET')(endpoint);
    const responseBody: LifetimeToplistingCreditUsage = await response.json();

    return responseBody;
  };
